import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { useParams, useNavigate } from "react-router-dom";
import { getInsfromExamId, checkExamAvailability } from "../../../api";
import { 
  CheckCircleIcon, 
  ExclamationTriangleIcon,
  ClockIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

const ExamInstructions = () => {
  const { eid } = useParams();
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAvailable, setIsAvailable] = useState(false);
  const [hasAgreed, setHasAgreed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const user = JSON.parse(sessionStorage.getItem("user"));
    if (!user) {
      Swal.fire(
        "Unauthorized",
        "Please log in to view the exam instructions.",
        "warning"
      );
      navigate("/login");
      return;
    }

    const loadExamInstructions = async () => {
      try {
        if (!eid) {
          Swal.fire("Error", "No Exam ID provided", "error");
          navigate("/student/exam");
          return;
        }

        console.log('🔍 Loading instructions for exam:', eid);

        const availabilityResult = await checkExamAvailability(eid);
        console.log('📊 Availability:', availabilityResult);
        
        if (!availabilityResult.available) {
          Swal.fire({
            icon: 'warning',
            title: 'Exam Not Available',
            text: availabilityResult.message,
            confirmButtonText: 'Back to Exams'
          }).then(() => {
            navigate("/student/exam");
          });
          return;
        }

        setIsAvailable(true);

        const data = await getInsfromExamId(eid);
        console.log('✅ Exam data received:', data);
        setExam(data);
        
      } catch (error) {
        console.error('❌ Error:', error);
        Swal.fire("Error", "Failed to fetch exam instructions", "error");
        navigate("/student/exam");
      } finally {
        setLoading(false);
      }
    };

    loadExamInstructions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eid, navigate]);

  const handleStartExam = async () => {
    if (!hasAgreed) {
      Swal.fire({
        icon: 'warning',
        title: 'Agreement Required',
        text: 'Please read and agree to the instructions before starting the exam.',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    try {
      const availabilityResult = await checkExamAvailability(eid);
      
      if (!availabilityResult.available) {
        Swal.fire({
          icon: 'warning',
          title: 'Exam Not Available',
          text: availabilityResult.message
        });
        return;
      }

      navigate(`/student/exam/${eid}`);
    } catch (error) {
      Swal.fire("Error", "Failed to verify exam availability", "error");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="relative">
          <div className="w-20 h-20 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <DocumentTextIcon className="w-8 h-8 text-blue-600" />
          </div>
        </div>
        <p className="mt-4 text-gray-600 font-medium">Loading exam instructions...</p>
      </div>
    );
  }

  if (!exam || !isAvailable) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="bg-white rounded-2xl shadow-2xl p-10 text-center max-w-md">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ExclamationTriangleIcon className="w-12 h-12 text-red-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-3">Exam Not Available</h3>
          <p className="text-gray-600 mb-6">This exam is currently not accessible.</p>
          <button
            onClick={() => navigate("/student/exam")}
            className="px-8 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-semibold shadow-lg transition-all"
          >
            Back to Exams
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        
        {/* Header Card */}
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden mb-6 border-2 border-blue-200">
          <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg">
                <DocumentTextIcon className="w-7 h-7 text-blue-600" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white">Exam Instructions</h2>
                <p className="text-blue-100 text-sm mt-1">Please read carefully before proceeding</p>
              </div>
            </div>
          </div>
        </div>

        {/* Exam Name and Details Card */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-gray-200">
          <h3 className="text-3xl font-bold text-gray-800 mb-4">{exam.name}</h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Duration */}
            <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-5 hover:shadow-md transition-shadow">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <ClockIcon className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs text-orange-700 font-semibold uppercase tracking-wide">Duration</p>
                  <p className="text-2xl font-bold text-gray-800">{exam.duration} min</p>
                </div>
              </div>
            </div>

            {/* Total Marks */}
            <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-5 hover:shadow-md transition-shadow">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <DocumentTextIcon className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-purple-700 font-semibold uppercase tracking-wide">Total Marks</p>
                  <p className="text-2xl font-bold text-gray-800">{exam.tmarks}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Instructions Card */}
        <div className="bg-white shadow-xl rounded-2xl p-6 mb-6 border border-gray-200">
          <div className="flex items-center space-x-2 mb-4">
            <DocumentTextIcon className="w-6 h-6 text-blue-600" />
            <h4 className="text-xl font-bold text-gray-800">Detailed Instructions</h4>
          </div>
          <div 
            className="prose prose-sm sm:prose lg:prose-lg max-w-none text-gray-700 bg-gray-50 p-5 rounded-lg border border-gray-200"
            dangerouslySetInnerHTML={{ 
              __html: exam.content || exam.instruction || '<p class="text-gray-500 italic">No specific instructions provided.</p>' 
            }}
          />
        </div>

        {/* Important Guidelines */}
        <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-6 mb-6 shadow-md">
          <div className="flex items-center space-x-2 mb-3">
            <ExclamationTriangleIcon className="w-6 h-6 text-yellow-700" />
            <h4 className="text-lg font-bold text-yellow-800">Important Guidelines</h4>
          </div>
          <ul className="space-y-2 text-sm text-yellow-900">
            <li className="flex items-start space-x-2">
              <CheckCircleIcon className="w-5 h-5 text-yellow-700 flex-shrink-0 mt-0.5" />
              <span>Ensure you have a stable internet connection throughout the exam</span>
            </li>
            <li className="flex items-start space-x-2">
              <CheckCircleIcon className="w-5 h-5 text-yellow-700 flex-shrink-0 mt-0.5" />
              <span>Do not refresh or close the browser tab during the exam</span>
            </li>
            <li className="flex items-start space-x-2">
              <CheckCircleIcon className="w-5 h-5 text-yellow-700 flex-shrink-0 mt-0.5" />
              <span>Once you start, the timer will begin counting down immediately</span>
            </li>
            <li className="flex items-start space-x-2">
              <CheckCircleIcon className="w-5 h-5 text-yellow-700 flex-shrink-0 mt-0.5" />
              <span>You can navigate between questions and mark them for review</span>
            </li>
            <li className="flex items-start space-x-2">
              <CheckCircleIcon className="w-5 h-5 text-yellow-700 flex-shrink-0 mt-0.5" />
              <span>Submit your exam before the timer expires to avoid auto-submission</span>
            </li>
          </ul>
        </div>

        {/* Agreement Checkbox */}
        <div className="bg-white shadow-xl rounded-2xl p-6 mb-6 border-2 border-blue-200">
          <label className="flex items-start space-x-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={hasAgreed}
              onChange={(e) => setHasAgreed(e.target.checked)}
              className="w-6 h-6 text-blue-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer mt-0.5"
            />
            <div className="flex-1">
              <p className="text-gray-800 font-semibold group-hover:text-blue-600 transition-colors">
                I have read and understood all the instructions
              </p>
              <p className="text-sm text-gray-600 mt-1">
                By checking this box, I confirm that I agree to follow all examination guidelines and rules.
              </p>
            </div>
          </label>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0 sm:space-x-4">
          <button
            onClick={() => navigate("/student/exam")}
            className="w-full sm:w-auto px-8 py-4 bg-gray-500 text-white rounded-xl hover:bg-gray-600 transition-all font-semibold shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
          >
            <span>← Back to Exams</span>
          </button>
          
          <button
            onClick={handleStartExam}
            disabled={!hasAgreed}
            className={`w-full sm:w-auto px-10 py-4 rounded-xl transition-all font-bold shadow-xl flex items-center justify-center space-x-2 ${
              hasAgreed
                ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700 hover:shadow-2xl hover:scale-105 active:scale-95'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <span>Start Exam</span>
            <span>→</span>
          </button>
        </div>

        {/* Warning Message */}
        {!hasAgreed && (
          <div className="mt-4 bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
            <p className="text-sm text-red-700 flex items-center space-x-2">
              <ExclamationTriangleIcon className="w-5 h-5" />
              <span>You must agree to the instructions before starting the exam</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExamInstructions;
